import { Checkbox, Divider, IconButton, makeStyles } from "@material-ui/core";
import { DeleteIcon, EditIcon } from "../../../../icons";
import { GroupBase, OptionProps, components } from "react-select5";

export function CheckboxOptionComponent<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  showDivider = true,
  ...props
}: OptionProps<Option, IsMulti, Group> & {
  onEdit?: (option: Option) => void;
  onDelete?: (option: Option) => void;
  showDivider?: boolean;
}) {
  const classes = useStyles();
  const { children, onDelete, onEdit, ...rest } = props;

  // We are removing onMouseMove and onMouseOver from innerProps because they cause the whole
  // list to re-render everytime the user hovers over an option. This is a performance issue.
  // See https://github.com/JedWatson/react-select/issues/3128#issuecomment-451936743
  delete props.innerProps.onMouseMove;
  delete props.innerProps.onMouseOver;

  if (!children) {
    return null;
  }

  const _onEdit = (option: Option, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (onEdit) onEdit(option);
  };

  const _onDelete = (option: Option, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (onDelete) onDelete(option);
  };

  return (
    <>
      <components.Option {...rest} className={classes.root}>
        <div className={classes.root}>
          <div className={classes.checkboxContainer}>
            <Checkbox checked={props.isSelected} style={{ padding: "0rem" }}></Checkbox>
            <span style={{ whiteSpace: "nowrap" }}>{props.children}</span>
          </div>
          {(onEdit || onDelete) && (
            <div className={classes.iconContainer}>
              {onEdit && (
                <IconButton
                  onClick={(e) => _onEdit(rest.data, e)}
                  className={classes.iconButton}
                >
                  <EditIcon className={classes.icon} />
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  onClick={(e) => _onDelete(rest.data, e)}
                  className={classes.iconButton}
                >
                  <DeleteIcon className={classes.icon} />
                </IconButton>
              )}
            </div>
          )}
        </div>
      </components.Option>
      {showDivider && <Divider />}
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.5rem",
    wordBreak: "break-word"
  },
  checkboxContainer: {
    display: "flex",
    gap: "0.5rem",
    alignItems: "center"
  },
  iconButton: {
    padding: "0.25rem",
    zIndex: 9999
  },
  iconContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0rem"
  },
  icon: {
    color: theme.palette.primary.main,
    width: "1.5rem",
    height: "1.5rem",
    "&:hover": {
      cursor: "pointer"
    },
    zIndex: 1000
  }
}));
